import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Mic, Brain, Code2, Play, Loader2, Sparkles, ChevronDown,
  FileUp, X, CheckCircle2, Puzzle, ImagePlus, Activity,
  HelpCircle, ExternalLink, ListChecks, Copy, Check,
} from "lucide-react";

const MODES = [
  { id: "live",   label: "Live",   icon: Mic,   desc: "Real-time AI help during a real interview" },
  { id: "mock",   label: "Mock",   icon: Brain, desc: "AI plays the interviewer, practice anytime" },
  { id: "coding", label: "Coding", icon: Code2, desc: "Algorithms, system design, optimal solutions" },
];

const SCENARIOS = [
  "General Purpose", "Behavioral", "System Design", "Frontend", "Backend",
  "Product Manager", "Consulting Case", "Data Science",
];

// Display-only choices. Interview sessions are always routed to the backend's
// configured production model, regardless of this selection.
const MODELS = [
  { id: "gpt-6-astra", label: "GPT-6 Astra", group: "OpenAI" },
  { id: "gpt-6.1-sol", label: "GPT-6.1 Sol", group: "OpenAI" },
  { id: "gpt-6-luna", label: "GPT-6 Luna", group: "OpenAI" },
  { id: "gpt-5.6-sol", label: "GPT-5.6 Sol", group: "OpenAI" },
  { id: "claude-opus-5-5", label: "Claude Opus 5.5", group: "Anthropic" },
  { id: "claude-fable-5-1", label: "Claude Fable 5.1", group: "Anthropic" },
  { id: "claude-fable-5", label: "Claude Fable 5", group: "Anthropic" },
  { id: "claude-opus-5", label: "Claude Opus 5", group: "Anthropic" },
];

const MODEL_GROUPS = ["OpenAI", "Anthropic"];
const GROUP_COLORS = { OpenAI: "#10a37f", Anthropic: "#c96442" };


// ── Model dropdown ────────────────────────────────────────────────────────────
function ModelDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = MODELS.find((m) => m.id === value);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="model-select">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 border border-white/10 bg-transparent text-sm hover:border-white/20 transition"
      >
        <Sparkles className="w-3.5 h-3.5 text-[#D3FF24]" />
        <span className="text-white">{selected?.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 max-h-[min(70vh,32rem)] overflow-y-auto bg-[#111] border border-white/10 z-50 py-2 shadow-2xl">
          {MODEL_GROUPS.map((group) => (
            <div key={group}>
              <div className="px-4 py-1.5 text-[10px] uppercase tracking-widest font-bold" style={{ color: GROUP_COLORS[group] }}>
                {group}
              </div>
              {MODELS.filter((m) => m.group === group).map((m) => (
                <button key={m.id} onClick={() => { onChange(m.id); setOpen(false); }}
                  className={`w-full text-left px-5 py-2 text-sm transition ${value === m.id ? "text-white bg-white/5" : "text-neutral-400 hover:text-white hover:bg-white/[0.03]"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Single multi-file upload zone ────────────────────────────────────────────
function DocUploadZone({ docs, loading, onUpload, onRemove }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = (files) => {
    Array.from(files).forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["pdf", "docx", "doc", "txt"].includes(ext)) {
        toast.error(`${file.name}: unsupported type. Use PDF, DOCX, or TXT.`);
        return;
      }
      onUpload(file);
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-2">
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed cursor-pointer transition-all flex flex-col items-center justify-center gap-3 py-8 px-6
          ${dragging ? "border-[#D3FF24]/60 bg-[#D3FF24]/[0.04]" : "border-white/10 hover:border-white/20 hover:bg-white/[0.01]"}`}
      >
        {loading ? (
          <>
            <Loader2 className="w-8 h-8 text-[#D3FF24] animate-spin" />
            <p className="text-sm text-neutral-400">Parsing document…</p>
          </>
        ) : (
          <>
            <FileUp className="w-8 h-8 text-neutral-600" strokeWidth={1.5} />
            <div className="text-center">
              <p className="text-sm text-neutral-300 font-medium">Drop files here or click to browse</p>
              <p className="text-xs text-neutral-600 mt-1">Resume, cover letter, job description — any documents · PDF, DOCX, TXT</p>
            </div>
          </>
        )}
      </div>
      <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.doc,.txt" className="hidden"
        onChange={(e) => { if (e.target.files.length) handleFiles(e.target.files); e.target.value = ""; }} />

      {/* Uploaded files list */}
      {docs.length > 0 && (
        <div className="border border-white/10 divide-y divide-white/5">
          {docs.map((doc, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <CheckCircle2 className="w-4 h-4 text-[#D3FF24] shrink-0" strokeWidth={1.75} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-white truncate">{doc.filename}</div>
                <div className="text-[10px] text-neutral-500 mt-0.5">{doc.words.toLocaleString()} words extracted</div>
              </div>
              <button onClick={() => onRemove(i)} className="text-neutral-600 hover:text-red-400 transition shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Chrome Extension accordion ───────────────────────────────────────────────
function ExtensionAccordion() {
  const [open, setOpen] = useState(false);

  return (
    <div className="pb-7">
      {/* Header row — always visible, click to toggle */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 text-left group"
      >
        <Puzzle className="w-4 h-4 text-neutral-500 shrink-0" strokeWidth={1.75} />
        <span className="text-sm font-semibold text-white flex-1">Chrome Extension</span>
        <span className="text-[9px] uppercase tracking-widest font-bold text-[#D3FF24] border border-[#D3FF24]/30 bg-[#D3FF24]/[0.04] px-2 py-0.5">Available</span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-neutral-600 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {/* Collapsible body */}
      {open && (
        <div className="mt-4 ml-[26px] flex flex-col gap-3">
          <p className="text-xs text-neutral-500">
            Capture any webpage, question, or coding problem directly into your session with one click. Works on LeetCode, HackerRank, and any browser tab.
          </p>

          <div className="flex items-center gap-3">
            <a
              href="/wingman-extension.zip"
              download="wingman-extension.zip"
              className="flex items-center gap-2 border border-[#D3FF24]/40 bg-[#D3FF24]/[0.04] px-4 py-2 text-xs text-[#D3FF24] font-semibold hover:bg-[#D3FF24]/[0.08] transition"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Download Extension (.zip)
            </a>
            <span className="text-[10px] text-neutral-600">Chrome · Free · 14 KB</span>
          </div>

          <div className="border border-white/[0.06] bg-white/[0.01] px-4 py-3 text-[11px] text-neutral-500 space-y-1.5 leading-relaxed">
            <div className="text-neutral-400 font-semibold text-[10px] uppercase tracking-widest mb-2">Install steps</div>
            <div><span className="text-neutral-300">1.</span> Download the zip and extract it to a permanent folder</div>
            <div><span className="text-neutral-300">2.</span> Open <span className="font-mono text-neutral-400">chrome://extensions</span> → enable <span className="text-neutral-300 font-semibold">Developer Mode</span></div>
            <div><span className="text-neutral-300">3.</span> Click <span className="text-neutral-300 font-semibold">Load unpacked</span> → select the extracted folder</div>
            <div><span className="text-neutral-300">4.</span> Click the Wingman icon in the toolbar → paste the credentials below</div>
          </div>

          <ExtCredentials />
        </div>
      )}
    </div>
  );
}

// ── Extension credentials box ─────────────────────────────────────────────────
function CopyField({ label, value, masked }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600 mb-1.5">{label}</div>
      <div className="flex items-center gap-0 border border-white/10">
        <div className="flex-1 px-3 py-2 font-mono text-[11px] text-neutral-400 truncate bg-black/40 select-all">
          {masked ? `${value.slice(0, 12)}${"•".repeat(Math.min(24, value.length - 12))}` : value}
        </div>
        <button
          onClick={copy}
          title="Copy"
          className="px-3 py-2 border-l border-white/10 text-neutral-500 hover:text-[#D3FF24] hover:bg-[#D3FF24]/[0.04] transition shrink-0"
        >
          {copied
            ? <Check className="w-3.5 h-3.5 text-[#D3FF24]" strokeWidth={2.5} />
            : <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />}
        </button>
      </div>
    </div>
  );
}

function ExtCredentials() {
  const appUrl = window.location.origin;
  const token  = localStorage.getItem("token") || "";

  return (
    <div className="border border-white/10 bg-[#0D0D0D] px-4 py-4 space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-1.5 h-1.5 rounded-full bg-[#D3FF24]" />
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#D3FF24]">
          Extension Credentials
        </span>
      </div>
      <p className="text-[11px] text-neutral-600 leading-relaxed -mt-1">
        Paste these into the extension popup after installing to connect it to your account.
      </p>
      <CopyField label="App URL" value={appUrl} masked={false} />
      <CopyField label="Auth Token" value={token} masked={true} />
    </div>
  );
}

// ── Toggle pill ──────────────────────────────────────────────────────────────
function Toggle({ enabled, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`relative w-9 h-5 shrink-0 rounded-full transition-colors duration-200 ${enabled ? "bg-[#D3FF24]" : "bg-white/10"}`}
    >
      <span className={`absolute top-0.5 w-4 h-4 rounded-full transition-transform duration-200 ${enabled ? "translate-x-[18px] bg-black" : "translate-x-0.5 bg-neutral-500"}`} />
    </button>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") || "live");

  useEffect(() => {
    const m = searchParams.get("mode");
    if (m) setMode(m);
  }, [searchParams]);

  const [scenario, setScenario]   = useState("General Purpose");
  const [company, setCompany]     = useState("");
  const [position, setPosition]   = useState("");
  const [objective, setObjective] = useState("");
  const [model, setModel]         = useState("gpt-6.1-sol");
  const [busy, setBusy]           = useState(false);

  // Documents: flat array of {filename, text, chars, words, doc_type}
  const [documents, setDocuments] = useState([]);
  const [docLoading, setDocLoading] = useState(false);

  // Advanced
  const [advImage, setAdvImage]           = useState(null); // {file, preview}
  const [imageContext, setImageContext]   = useState("");
  const [speechReflection, setSpeechReflection] = useState({ fillerWords: false, pace: false, confidence: false });
  const [qnaPrep, setQnaPrep]             = useState("");
  const imgInputRef = useRef(null);

  // Listen for captures injected by the Chrome extension
  useEffect(() => {
    const handler = (e) => {
      const { image, context, sourceTitle } = e.detail || {};
      if (image) {
        setAdvImage({ file: null, preview: image });
        if (context) setImageContext(context);
        toast.success(`Capture received from extension${sourceTitle ? ` — ${sourceTitle.slice(0, 40)}` : ''}`);
      }
    };
    window.addEventListener('wingman-capture', handler);
    return () => window.removeEventListener('wingman-capture', handler);
  }, []);

  const uploadDoc = async (file) => {
    setDocLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("doc_type", "document");
      const res = await fetch("/api/documents/parse", {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to parse file");
      }
      const data = await res.json();
      setDocuments((d) => [...d, data]);
      toast.success(`${data.filename} · ${data.words.toLocaleString()} words extracted`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDocLoading(false);
    }
  };

  const removeDoc = (idx) => setDocuments((d) => d.filter((_, i) => i !== idx));

  const start = async () => {
    if (!company || !position) { toast.error("Company and Position are required"); return; }
    setBusy(true);
    try {
      const docs = documents.map((d) => ({
        doc_type: d.doc_type || "document",
        filename: d.filename,
        text: d.text,
      }));
      const anySpeech = Object.values(speechReflection).some(Boolean);
      const { data } = await api.post("/sessions", {
        // The picker is decorative; do not send its selection to production.
        mode, scenario, company, position, objective,
        documents: docs,
        ...(imageContext.trim() && { image_context: imageContext.trim() }),
        ...(anySpeech && { speech_reflection: speechReflection }),
        ...(qnaPrep.trim() && { qna_prep: qnaPrep.trim() }),
      });
      nav(`/app/sessions/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start session");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-10 py-10 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-5xl font-black tracking-tighter">Interview</h1>
        <ModelDropdown value={model} onChange={setModel} />
      </div>
      <p className="text-neutral-500 mb-10">Real-time AI help for live, mock, and coding interviews.</p>

      {/* Mode tabs */}
      <div className="grid grid-cols-3 gap-px bg-white/10 mb-10">
        {MODES.map((m) => {
          const active = mode === m.id;
          return (
            <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setMode(m.id)}
              className={`p-5 text-left transition ${active ? "bg-[#121212] border-l-2 border-[#D3FF24]" : "bg-[#0A0A0A] hover:bg-[#0E0E0E] border-l-2 border-transparent"}`}>
              <m.icon className={`w-5 h-5 mb-3 ${active ? "text-[#D3FF24]" : "text-neutral-500"}`} strokeWidth={1.75} />
              <div className="font-display font-semibold text-lg">{m.label}</div>
              <div className="text-xs text-neutral-500 mt-1">{m.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Hint */}
      <p className="text-sm font-bold mb-6 leading-relaxed text-[#D3FF24]" style={{ textShadow: "0 0 18px rgba(211,255,36,0.45)" }}>
        The interview setup helps the AI to decode your accurate background and give you the best possible answers that gets you selected.
      </p>

      {/* ── Box 1: Interview Setup ── */}
      <div className="border border-white/10 p-8 mb-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-7 h-7 border border-white/15 text-xs flex items-center justify-center font-mono">1</div>
          <h2 className="font-display text-2xl font-semibold">Interview Setup</h2>
        </div>

        <div className="space-y-6">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Scenario</label>
            <select data-testid="scenario-select" value={scenario} onChange={(e) => setScenario(e.target.value)}
              className="mt-2 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none">
              {SCENARIOS.map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
            </select>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Company *</label>
              <input data-testid="company-input" value={company} onChange={(e) => setCompany(e.target.value)}
                placeholder="e.g. Stripe"
                className="mt-2 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Position *</label>
              <input data-testid="position-input" value={position} onChange={(e) => setPosition(e.target.value)}
                placeholder="e.g. Senior Backend Engineer"
                className="mt-2 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Objective</label>
            <textarea data-testid="objective-input" rows={4} value={objective} onChange={(e) => setObjective(e.target.value)}
              placeholder="Additional info (e.g. Behavioral round with founder, technical round with CTO)"
              className="mt-2 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none resize-none" />
          </div>
        </div>
      </div>

      {/* ── Box 2: Documents ── */}
      <div className="border border-white/10 p-8 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 border border-white/15 text-xs flex items-center justify-center font-mono">2</div>
            <h2 className="font-display text-2xl font-semibold">Documents</h2>
          </div>
          {documents.length > 0 && (
            <div className="flex items-center gap-1.5 border border-[#D3FF24]/30 bg-[#D3FF24]/[0.04] px-3 py-1">
              <CheckCircle2 className="w-3 h-3 text-[#D3FF24]" strokeWidth={2} />
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#D3FF24]">
                {documents.length} doc{documents.length > 1 ? "s" : ""} loaded
              </span>
            </div>
          )}
        </div>
        <p className="text-neutral-500 text-sm mb-6 ml-10">
          Upload your resume, cover letter, job description, or any document — the AI reads them and uses your real background to craft personalised answers during the session.
        </p>

        <DocUploadZone
          docs={documents}
          loading={docLoading}
          onUpload={uploadDoc}
          onRemove={removeDoc}
        />

        {documents.length === 0 && (
          <p className="text-[10px] text-neutral-700 mt-3">
            Optional — skip and start without documents if you prefer.
          </p>
        )}
      </div>

      {/* ── Box 3: Advanced ── */}
      <div className="border border-white/10 p-8 mb-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-7 h-7 border border-white/15 text-xs flex items-center justify-center font-mono">3</div>
          <h2 className="font-display text-2xl font-semibold">Advanced</h2>
          <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600 border border-white/10 px-2 py-0.5">Optional</span>
        </div>

        <div className="space-y-0 divide-y divide-white/[0.05]">

          {/* ── Chrome Extension ── */}
          <ExtensionAccordion />

          {/* ── Image + Context ── */}
          <div className="py-7">
            <div className="flex items-center gap-2.5 mb-2">
              <ImagePlus className="w-4 h-4 text-neutral-500 shrink-0" strokeWidth={1.75} />
              <span className="text-sm font-semibold text-white">Image &amp; Context</span>
            </div>
            <p className="text-xs text-neutral-500 mb-4 ml-[26px]">
              Upload a screenshot of a coding problem, diagram, or question. Add context to help the AI understand what you need.
            </p>
            <div className="ml-[26px] space-y-3">
              {/* Image drop zone */}
              <div
                onClick={() => imgInputRef.current?.click()}
                className={`cursor-pointer border border-dashed transition-all flex items-center justify-center gap-3 h-28 relative overflow-hidden
                  ${advImage ? "border-[#D3FF24]/40 bg-[#D3FF24]/[0.02]" : "border-white/10 hover:border-white/20"}`}
              >
                {advImage ? (
                  <>
                    <img src={advImage.preview} alt="preview" className="h-full w-full object-contain" />
                    <button
                      onClick={(e) => { e.stopPropagation(); URL.revokeObjectURL(advImage.preview); setAdvImage(null); }}
                      className="absolute top-2 right-2 bg-black/70 border border-white/20 text-neutral-400 hover:text-red-400 transition p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <div className="text-center">
                    <ImagePlus className="w-6 h-6 text-neutral-700 mx-auto mb-1" strokeWidth={1.5} />
                    <p className="text-xs text-neutral-500">Click to upload image</p>
                    <p className="text-[10px] text-neutral-700 mt-0.5">PNG, JPG, WebP</p>
                  </div>
                )}
              </div>
              <input ref={imgInputRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setAdvImage({ file: f, preview: URL.createObjectURL(f) });
                  e.target.value = "";
                }}
              />
              {/* Context textarea */}
              <textarea
                rows={3}
                value={imageContext}
                onChange={(e) => setImageContext(e.target.value)}
                placeholder="Describe the image or add context (e.g. 'This is a system design question about a URL shortener')"
                className="w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none resize-none text-neutral-300 placeholder:text-neutral-700"
              />
            </div>
          </div>

          {/* ── Speech Reflection ── */}
          <div className="py-7">
            <div className="flex items-center gap-2.5 mb-2">
              <Activity className="w-4 h-4 text-neutral-500 shrink-0" strokeWidth={1.75} />
              <span className="text-sm font-semibold text-white">Speech Reflection</span>
            </div>
            <p className="text-xs text-neutral-500 mb-5 ml-[26px]">
              Enable real-time feedback on your speaking patterns during the session.
            </p>
            <div className="ml-[26px] space-y-3.5">
              {[
                { key: "fillerWords", label: "Filler word detection", desc: "Flags um, uh, like, you know in real time" },
                { key: "pace",        label: "Speaking pace monitor", desc: "Alerts when you're speaking too fast or too slow" },
                { key: "confidence",  label: "Confidence scoring",    desc: "Analyses your tone and delivery for each answer" },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm text-neutral-300">{label}</div>
                    <div className="text-[11px] text-neutral-600 mt-0.5">{desc}</div>
                  </div>
                  <Toggle
                    enabled={speechReflection[key]}
                    onToggle={() => setSpeechReflection((s) => ({ ...s, [key]: !s[key] }))}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* ── Q&A Prep ── */}
          <div className="pt-7">
            <div className="flex items-center gap-2.5 mb-2">
              <ListChecks className="w-4 h-4 text-neutral-500 shrink-0" strokeWidth={1.75} />
              <span className="text-sm font-semibold text-white">Q&amp;A Prep</span>
            </div>
            <p className="text-xs text-neutral-500 mb-4 ml-[26px]">
              Pre-load questions or topics you want the AI to focus on. It will prioritise these during the session.
            </p>
            <textarea
              rows={5}
              value={qnaPrep}
              onChange={(e) => setQnaPrep(e.target.value)}
              placeholder={"Enter questions, topics, or areas to focus on:\n• Tell me about a time you led a team through ambiguity\n• System design: design Twitter's timeline\n• Explain your approach to code reviews"}
              className="ml-[26px] w-[calc(100%-26px)] bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none resize-none text-neutral-300 placeholder:text-neutral-700"
            />
          </div>

        </div>
      </div>

      {/* Start */}
      <div className="flex items-center justify-end">
        <button
          data-testid="start-interview-button"
          onClick={start}
          disabled={busy}
          className="group inline-flex items-center gap-3 bg-premium text-white px-10 py-4 font-semibold uppercase tracking-widest text-xs hover:brightness-110 transition disabled:opacity-60 glow-blue"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" fill="currentColor" />}
          Start
        </button>
      </div>
    </div>
  );
}
